/**
 * Persistent dedup of `(fromUserId, messageId)` pairs across Service Worker
 * restarts.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/NSEDuplicateGuard.swift`.
 *
 * Web push can re-fire the same push to the SW (network retry, SW restart,
 * push service retry) within a short window. Without this guard the user
 * would see the same notification twice. The guard uses IndexedDB because:
 *
 *   - The SW process is short-lived and gets a fresh JS heap on each
 *     activation, so an in-memory cache is useless across push events that
 *     span SW restarts.
 *   - IndexedDB is accessible from the SW context and persists across
 *     restarts (mirrors iOS `NSEDuplicateGuard`'s file-per-pair layout).
 *
 * Layout:  one IndexedDB object store (`sw-dedup`) keyed by the hex-encoded
 * SHA-256 digest of `"<fromUserId> <messageId>"`. Each record stores
 * the ISO-8601 timestamp at which the pair was first processed.
 *
 * Cleanup:  opportunistic prune of entries older than 2× the dedup window on
 * each `shouldDeliver` call, mirroring iOS prune-on-shouldProcess.
 *
 * Pure IndexedDB only.  No `self.registration.showNotification()` calls.
 */

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

/**
 * Derive a stable, fixed-length key string for a `(fromUserId, messageId)`
 * pair by SHA-256-hashing `"<fromUserId> <messageId>"` (the null byte
 * separator makes the two parts unambiguous — mirrors iOS SHA256 hashing).
 *
 * Returns a lowercase hex string, e.g.
 * `"4e07408562bedb8b60ce05c1d2..."`
 */
async function dedupKey(fromUserId: string, messageId: string): Promise<string> {
  const raw = `${fromUserId} ${messageId}`;
  const bytes = new TextEncoder().encode(raw);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = "fr.kaether.sw-dedup";
const STORE_NAME = "entries";
const DB_VERSION = 1;

interface DedupRecord {
  /** Hex-encoded SHA-256 key (the IDB primary key). */
  key: string;
  /** ISO-8601 timestamp of when the pair was first processed. */
  processedAt: string;
}

function openDb(dbName: string = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(
  db: IDBDatabase,
  key: string,
): Promise<DedupRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as DedupRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, record: DedupRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll(db: IDBDatabase): Promise<DedupRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as DedupRecord[]);
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbClearAll(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// SWDuplicateGuard
// ---------------------------------------------------------------------------

export interface SWDuplicateGuardKey {
  readonly fromUserId: string;
  readonly messageId: string;
}

/**
 * Options for constructing a `SWDuplicateGuard`. Mostly for testing:
 * supply a different `dbName` to get a fully isolated IndexedDB instance.
 */
export interface SWDuplicateGuardOptions {
  /** Override the IndexedDB database name (use for isolated tests). */
  readonly dbName?: string;
  /** Dedup window in seconds. Default: 30 (mirrors iOS). */
  readonly dedupWindowSeconds?: number;
}

/**
 * Service Worker persistent dedup guard.
 *
 * Usage (in the SW `push` event handler):
 *
 * ```ts
 * const guard = new SWDuplicateGuard();
 * const key = { fromUserId: payload.fromUserId, messageId: payload.messageId };
 * if (!(await guard.shouldDeliver(key))) return; // drop duplicate
 * // … decrypt + render …
 * await guard.recordProcessed(key);
 * ```
 */
export class SWDuplicateGuard {
  private readonly dbName: string;
  private readonly dedupWindowMs: number;

  constructor(options: SWDuplicateGuardOptions = {}) {
    this.dbName = options.dbName ?? DB_NAME;
    this.dedupWindowMs = (options.dedupWindowSeconds ?? 30) * 1000;
  }

  /**
   * Returns `true` if this `(fromUserId, messageId)` pair has NOT been
   * processed within the dedup window — the caller should deliver the
   * notification.
   *
   * Returns `false` if the same pair was recorded recently — the caller
   * should silently drop the duplicate re-fire.
   *
   * Opportunistically prunes entries older than 2× the dedup window on
   * every call, mirroring iOS `pruneExpired`.
   *
   * @param key  The `(fromUserId, messageId)` pair to check.
   * @param now  Override the current timestamp (ms). Defaults to `Date.now()`.
   */
  async shouldDeliver(
    key: SWDuplicateGuardKey,
    now: number = Date.now(),
  ): Promise<boolean> {
    const db = await openDb(this.dbName);
    try {
      // Prune stale entries first (mirrors iOS prune-on-shouldProcess).
      await this._pruneExpired(db, now);

      const hexKey = await dedupKey(key.fromUserId, key.messageId);
      const record = await idbGet(db, hexKey);
      if (record === undefined) {
        return true; // never seen before → deliver
      }
      const processedAt = Date.parse(record.processedAt);
      const age = now - processedAt;
      return age >= this.dedupWindowMs; // outside window → deliver again
    } finally {
      db.close();
    }
  }

  /**
   * Records that a `(fromUserId, messageId)` pair has been processed at
   * `now`. Called after a notification was rendered (or intentionally
   * suppressed).
   *
   * @param key  The `(fromUserId, messageId)` pair to record.
   * @param now  Override the current timestamp (ms). Defaults to `Date.now()`.
   */
  async recordProcessed(
    key: SWDuplicateGuardKey,
    now: number = Date.now(),
  ): Promise<void> {
    const db = await openDb(this.dbName);
    try {
      const hexKey = await dedupKey(key.fromUserId, key.messageId);
      const record: DedupRecord = {
        key: hexKey,
        processedAt: new Date(now).toISOString(),
      };
      await idbPut(db, record);
    } finally {
      db.close();
    }
  }

  /**
   * Remove ALL dedup entries (used by tests to reset state between cases).
   */
  async resetAll(): Promise<void> {
    const db = await openDb(this.dbName);
    try {
      await idbClearAll(db);
    } finally {
      db.close();
    }
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  /**
   * Prune records older than `2 × dedupWindowMs`. Mirrors iOS
   * `pruneExpired(now:)` which uses `2 * dedupWindowSeconds` as the
   * threshold — anything older cannot influence a future `shouldDeliver`
   * decision.
   */
  private async _pruneExpired(db: IDBDatabase, now: number): Promise<void> {
    const threshold = 2 * this.dedupWindowMs;
    const all = await idbGetAll(db);
    for (const record of all) {
      const age = now - Date.parse(record.processedAt);
      if (age > threshold) {
        await idbDelete(db, record.key);
      }
    }
  }
}
